// ============================================================================
// AvailabilityPicker — the structured WEEKLY picker (per-day available toggle +
// from/to time) used inside the create-client form. The codec lives in lib.ts;
// this is the UI. Mobile-first: each day is a full-width row that reveals its
// time inputs when toggled on, with a smooth height/opacity transition
// (reduced-motion safe). Self-contained in this page's folder + namespace.
// ============================================================================
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/cn";
import { DAY_KEYS, type DayKey, type WeeklyAvailability } from "./lib";

interface AvailabilityPickerProps {
  value: WeeklyAvailability;
  onChange: (next: WeeklyAvailability) => void;
}

export function AvailabilityPicker({ value, onChange }: AvailabilityPickerProps) {
  const { t } = useTranslation("clients");
  const reduce = useReducedMotion();

  const setDay = (day: DayKey, patch: Partial<WeeklyAvailability[DayKey]>) => {
    onChange({ ...value, [day]: { ...value[day], ...patch } });
  };

  return (
    <fieldset className="space-y-2">
      <legend className="mb-1.5 font-display text-sm font-bold text-body">
        {t("form.availability")}
      </legend>
      <p className="mb-2 text-sm text-body-soft">{t("form.availabilityHint")}</p>
      <ul className="space-y-2">
        {DAY_KEYS.map((day) => {
          const d = value[day];
          return (
            <li
              key={day}
              className={cn(
                "rounded-md p-3 ring-1 transition-colors",
                d.on ? "bg-coral-soft/50 ring-coral/25" : "bg-surface ring-line",
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={d.on}
                  aria-label={t(`days.${day}`)}
                  onClick={() => setDay(day, { on: !d.on })}
                  className="flex min-w-0 items-center gap-3"
                >
                  <span
                    className={cn(
                      "relative inline-flex h-6 w-11 shrink-0 items-center rounded-pill transition-colors duration-fast",
                      d.on
                        ? "bg-grad-coral shadow-volt"
                        : "bg-surface-sunken ring-1 ring-inset ring-line-strong",
                    )}
                  >
                    <motion.span
                      layout={!reduce}
                      transition={{ type: "spring", stiffness: 500, damping: 32 }}
                      className={cn(
                        "absolute h-5 w-5 rounded-full bg-white shadow-sm",
                        d.on ? "right-0.5" : "left-0.5",
                      )}
                    />
                  </span>
                  <span className="font-display text-sm font-bold text-body">
                    {t(`days.${day}`)}
                  </span>
                </button>

                {!d.on && (
                  <span className="text-xs font-medium text-body-faint">
                    {t("availability.off")}
                  </span>
                )}
              </div>

              <AnimatePresence initial={false}>
                {d.on && (
                  <motion.div
                    initial={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
                    animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
                    exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 flex items-end gap-3">
                      <TimeInput
                        label={t("availability.from")}
                        value={d.from}
                        onChange={(from) => setDay(day, { from })}
                      />
                      <span className="pb-2.5 text-body-faint">–</span>
                      <TimeInput
                        label={t("availability.to")}
                        value={d.to}
                        onChange={(to) => setDay(day, { to })}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

function TimeInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex-1">
      <span className="mb-1 block text-xs font-bold uppercase tracking-kicker text-body-soft">
        {label}
      </span>
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-11 w-full rounded-md bg-surface px-3 text-base text-body",
          "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast",
          "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none",
        )}
      />
    </label>
  );
}
